// The contrast maths, and the thresholds it is judged against.
//
// Kept apart from ./tool because that one needs fabric and a canvas to read
// pixels, and importing fabric drags in a DOM. Everything here is arithmetic on
// three numbers, so it stays checkable on its own - the same reason the geometry
// under ../geometry is duck-typed rather than fabric-aware.
//
// The thresholds live here rather than next to the sampling because they are what
// the ratios are compared with; a ratio and the line it has to clear are not worth
// much apart.

// WCAG 2.1 relative luminance. Perceptual rather than a plain RGB distance, so
// mid-grey text on a mid-blue shirt is judged the way an eye judges it.
function channelLuminance(value) {
	const c = value / 255;
	return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(r, g, b) {
	return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

// 1 = identical, 21 = black on white.
export function contrastRatio(a, b) {
	const la = relativeLuminance(a[0], a[1], a[2]);
	const lb = relativeLuminance(b[0], b[1], b[2]);
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// CIE L*a*b*, D65. Wanted for the two chroma axes: the ratio above is a comparison
// of brightness alone and says nothing about hue.
function toLab([r, g, b]) {
	// The luminance curve above uses the WCAG 0.03928 cutoff; sRGB proper puts it
	// at 0.04045. The difference is a rounding artefact of the spec, but keep each
	// formula to its own so neither is quietly wrong.
	const linear = v => {
		const c = v / 255;
		return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	};
	const R = linear(r), G = linear(g), B = linear(b);
	const x = (0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047;
	const y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
	const z = (0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883;
	const f = t => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
	const fx = f(x), fy = f(y), fz = f(z);
	return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

// How far apart two colours look, CIE76. Roughly: under 2.3 is the smallest
// difference an eye can pick up at all, and past about 25 they read as different
// colours rather than shades of one.
//
// CIE76 rather than the more even CIE2000 on purpose - the numbers this is asked
// to separate are 15 apart at the closest, so the extra machinery would buy
// nothing that could not be read off the result anyway.
export function colorDifference(a, b) {
	const [l1, a1, b1] = toLab(a);
	const [l2, a2, b2] = toLab(b);
	return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

// How badly one colour disappears into another, or null when it does not.
//
// Both signals have to agree. The contrast ratio is a comparison of brightness
// with the hue thrown away, so two colours from opposite sides of the wheel that
// happen to be equally bright score the lowest ratio there is: red #FF0000 on the
// Atoll shirt, rgb(5, 136, 185), comes out at 1.004 - and it is perfectly legible,
// because nothing about it is the same colour. That was reported as invisible.
//
// So a warning needs the colours to be close in both senses. Measured over the
// garments: everything that genuinely vanishes sits at a difference of 1 to 15
// (white on White 1.0, white on Ash 3.3, black on Black 7.7, dark grey on Black
// 10.8, black on Navy 11.6, navy on Navy 14.7), while red on Atoll is at 137.5.
export function severityFor(text, shirt, thresholds = DEFAULTS) {
	const ratio = contrastRatio(text, shirt);
	const difference = colorDifference(text, shirt);

	if (difference >= thresholds.distinctAbove) return { severity: null, ratio, difference };
	if (ratio < thresholds.invisibleBelow) return { severity: 'invisible', ratio, difference };
	if (ratio < thresholds.lowBelow) return { severity: 'low', ratio, difference };
	return { severity: null, ratio, difference };
}

export const DEFAULTS = {
	// Below this the text is effectively gone against the shirt.
	//
	// Measured off the garment images rather than picked: a photographed shirt is
	// never one flat value, so text the same colour as the shirt does not land
	// anywhere near a ratio of 1. Across the 31 colours of GI18500, black text on
	// the Black hoodie bottoms out at 1.109 and on Navy at 1.149; white text on
	// White at 1.104 and on Ash at 1.044. The first combination that is genuinely
	// readable, black on Maroon, does not go below 1.471.
	//
	// So there is a clear gap, and this sits in the middle of it. The old 1.1 was
	// under every one of those numbers - not one pixel of black on black could
	// reach it, which is why that combination never warned.
	invisibleBelow: 1.3,
	// Between the two it is readable up close but weak on a garment.
	lowBelow: 2.6,
	// Colour difference at which hue alone carries the text, whatever the two
	// brightnesses do. Everything that genuinely vanishes measures under 15; the
	// case this was added for, red on Atoll, is at 137. See severityFor.
	distinctAbove: 25,
	// Share of the glyph pixels that has to be affected before it is worth saying
	// anything - a seam or a print edge clipping a few pixels is not a problem.
	// It is also what keeps a heather shirt quiet: white text on Sport Grey has a
	// few flecks under the line and nothing else, at a coverage of 0.002.
	minCoverage: 0.1,
	// Sample every Nth pixel in both directions. 2 keeps a full check well under a
	// frame while still catching stripes a few pixels wide.
	sampleStep: 2
};
